import { useState, useCallback } from 'react';
import { NODE_W, NODE_H, NODE_W_ROOT, NODE_H_ROOT } from '../constants';

/**
 * Hook for drag-to-connect interaction on the canvas
 * @param {Object} scrollContainerRef - Ref to the scroll container
 * @param {Array} nodes - Current nodes
 * @param {Function} onCreateConnection - Called when a connection is confirmed
 */
export const useConnectInteraction = (scrollContainerRef, nodes, onCreateConnection, zoom = 1) => {
  const [connectState, setConnectState] = useState({
    active: false, fromId: null, fromX: 0, fromY: 0, curX: 0, curY: 0, targetId: null
  });

  const getSVGCoords = useCallback((e) => {
    const container = scrollContainerRef.current;
    if (!container) return { x: 0, y: 0 };
    const rect = container.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left + container.scrollLeft) / zoom,
      y: (e.clientY - rect.top + container.scrollTop) / zoom
    };
  }, [scrollContainerRef, zoom]);

  const startConnect = useCallback((nodeId, nodeX, nodeY, nodeType, e) => {
    e.stopPropagation();
    const { x, y } = getSVGCoords(e);
    const W = nodeType === 'problem' ? NODE_W_ROOT : NODE_W;
    setConnectState({
      active: true, fromId: nodeId,
      fromX: nodeX + W / 2, fromY: nodeY,
      curX: x, curY: y, targetId: null
    });
  }, [getSVGCoords]);

  const handleMouseMove = useCallback((e) => {
    if (!connectState.active) return;
    const { x, y } = getSVGCoords(e);
    const target = nodes.find(n => {
      if (n.id === connectState.fromId) return false;
      const W = n.type === 'problem' ? NODE_W_ROOT : NODE_W;
      const H = n.type === 'problem' ? NODE_H_ROOT : NODE_H;
      return Math.abs(n.x - x) <= W / 2 + 10 && Math.abs(n.y - y) <= H / 2 + 10;
    });
    setConnectState(prev => ({ ...prev, curX: x, curY: y, targetId: target?.id || null }));
  }, [connectState.active, connectState.fromId, getSVGCoords, nodes]);

  const handleMouseUp = useCallback(() => {
    if (!connectState.active) return;
    if (connectState.targetId) onCreateConnection(connectState.fromId, connectState.targetId);
    setConnectState({ active: false, fromId: null, fromX: 0, fromY: 0, curX: 0, curY: 0, targetId: null });
  }, [connectState, onCreateConnection]);

  return { connectState, startConnect, handleMouseMove, handleMouseUp };
};
